/**
 * Flap Portal getTokenV5 — wagmi/viem 并发 view（Multicall）
 * 解析逻辑对齐 api/quote_lib.php
 */
import { readContracts } from "@wagmi/core";
import { formatEther, formatUnits } from "viem";
import { wagmiConfig, BSC_ID } from "@/wagmi/config.js";
import { BBROKER_STOCK_CATALOG } from "@/lib/bbrokerStockCatalog.js";
import { FLAP_CRYPTO_QUOTE_OPTIONS } from "@/lib/flapQuoteSupport.js";
import { quoteTimestampMs } from "@/lib/quoteMcap.js";
import { knownQuoteDecimals, readQuoteDecimals, quoteDecimalsAbi } from "@/lib/quoteDecimals.js";

export const FLAP_PORTAL = "0xe2cE6ab80874Fa9Fa2aAE65D277Dd6B8e65C9De0";

/** 与链上 selector 0x5c4bc504 对齐的 view ABI */
export const portalAbi = [
  {
    type: "function",
    name: "getTokenV5",
    stateMutability: "view",
    inputs: [{ name: "token", type: "address" }],
    outputs: [
      { name: "status", type: "uint8" },
      { name: "reserve", type: "uint256" },
      { name: "circulating", type: "uint256" },
      { name: "price", type: "uint256" },
      { name: "unused", type: "uint256" },
      { name: "r", type: "uint256" },
      { name: "h", type: "uint256" },
      { name: "k", type: "uint256" },
      { name: "dexThresh", type: "uint256" },
      { name: "quoteToken", type: "address" },
    ],
  },
];

/** 与 api/quote_lib.php flap_quote_symbol 对齐：CRYPTO + 完整股票目录 + 遗留地址 */
const LEGACY_QUOTE_SYM = {
  "0x0000000000000000000000000000000000000000": "BNB",
  "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c": "BNB", // WBNB
  "0x55d398326f99059ff775485246999027b3197955": "USDT",
  "0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d": "USD1",
  "0xce24439f2d9c6a2289f741120fe202248b666666": "U",
  "0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c": "BTCB",
  "0x570a5d26f7765ecb712c0924e4de545b89fd43df": "SOL",
  "0x2170ed0880ac9a755fd29b2688956bd959f933f8": "ETH",
  "0xe614e2fc6c787035ff51f452e8e826bfd32d5283": "INTCB",
  "0xcdf2f3e0fa43c47a6662a91c9e4a7c5f69762699": "MUB",
  "0x3f53de71c126bdabae20f9cd64848d317f6c3238": "GOOGLB",
  "0x6786c0d38dcd4994d1e8fed481f6439a11a14a8a": "QQQB", // 旧 QQQB
};

export const QUOTE_SYM = {
  ...LEGACY_QUOTE_SYM,
  ...Object.fromEntries(
    FLAP_CRYPTO_QUOTE_OPTIONS.filter((c) => c.address)
      .map((c) => [String(c.address).toLowerCase(), c.symbol])
      .filter(([addr]) => /^0x[a-f0-9]{40}$/.test(addr))
  ),
  ...Object.fromEntries(
    BBROKER_STOCK_CATALOG.map((a) => [
      String(a.address || "").toLowerCase(),
      a.symbol,
    ]).filter(([addr]) => /^0x[a-f0-9]{40}$/.test(addr))
  ),
};

function isNativeQuote(addr) {
  const a = String(addr || "").toLowerCase();
  return (
    !a ||
    a === "0x0000000000000000000000000000000000000000" ||
    a === "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
  );
}

/** 解析报价币符号；未知 ERC20 保留 hint，切勿一律当成 BNB */
export function resolveQuoteSymbol(quoteToken, hint = "") {
  const a = String(quoteToken || "").toLowerCase();
  if (QUOTE_SYM[a]) return QUOTE_SYM[a];
  if (isNativeQuote(a)) return "BNB";
  const h = String(hint || "").trim();
  if (h && h !== "BNB") return h;
  if (a && /^0x[a-f0-9]{40}$/.test(a)) {
    return a.slice(2, 6).toUpperCase();
  }
  return "BNB";
}

function toEther(v) {
  try {
    return Number(formatEther(v ?? 0n));
  } catch {
    return 0;
  }
}

function curveReserveRequired(thresh, r, h, k) {
  const den = 1e9 + h - thresh;
  if (den <= 1e-18) return 0;
  return k / den - r;
}

export function parseTokenV5Result(raw, decimals = undefined) {
  if (!Array.isArray(raw) || raw.length < 10) return null;
  const [
    statusRaw,
    reserveRaw,
    circRaw,
    priceRaw,
    ,
    rRaw,
    hRaw,
    kRaw,
    threshRaw,
    quoteRaw,
  ] = raw;
  const status = Number(statusRaw) || 0;
  const quoteToken = String(quoteRaw || "").toLowerCase();
  const reserveDecimals = decimals === undefined ? knownQuoteDecimals(quoteToken) : decimals;
  const knownReserve = reserveDecimals != null && Number.isInteger(reserveDecimals) && reserveDecimals >= 0 && reserveDecimals <= 255;
  // Portal reserve is in ERC20 units; its price and curve parameters remain WAD.
  const reserveBnb = knownReserve ? Number(formatUnits(reserveRaw ?? 0n, reserveDecimals)) : null;
  const circulating = toEther(circRaw);
  const priceBnb = toEther(priceRaw);
  const r = toEther(rRaw);
  const h = toEther(hRaw);
  const k = toEther(kRaw);
  const dexThresh = toEther(threshRaw);
  const quoteSymbol = resolveQuoteSymbol(quoteToken);
  const fdvBnb = priceBnb * 1e9;
  const mcapBnb = priceBnb * circulating;
  const reserveRequired = curveReserveRequired(dexThresh, r, h, k);
  let progress = knownReserve ? 0 : null;
  if (knownReserve && reserveRequired > 1e-12) {
    progress = Math.min(100, Math.max(0, (reserveBnb / reserveRequired) * 100));
  } else if (knownReserve && dexThresh > 0) {
    progress = Math.min(100, (circulating / dexThresh) * 100);
  }
  // 股票底储备/进度保留更多位数，避免小额底池被 round 抹平
  return {
    status,
    reserveBnb,
    reserveDecimals: knownReserve ? reserveDecimals : null,
    circulating,
    priceBnb,
    fdvBnb: Number(fdvBnb.toFixed(6)),
    mcapBnb: Number(mcapBnb.toFixed(6)),
    dexThresh,
    reserveRequired: Number(reserveRequired.toFixed(8)),
    progress: progress == null ? null : Number(progress.toFixed(4)),
    listed: status >= 2,
    quoteToken,
    quoteSymbol,
    source: "wagmi",
  };
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * 并发读多币 getTokenV5（wagmi readContracts → Multicall）
 * 失败时缩小批次重试，再走 /api/quote.php；仍空则抛错供 UI 提示节点状态。
 * @param {string[]} tokens
 * onBatch receives only newly successful tokens, as soon as a batch completes.
 * Each quote carries its observed time in quoteUpdatedAt (milliseconds).
 * @param {{ chunkSize?: number, concurrency?: number, timeoutMs?: number, signal?: AbortSignal, onBatch?: (quotes: Object) => void }} [opts]
 */
export async function readFlapQuotesLive(tokens, opts = {}) {
  const list = [...new Set((tokens || []).map((t) => String(t || "").toLowerCase()))]
    .filter((t) => /^0x[a-f0-9]{40}$/.test(t));
  if (!list.length) return {};
  const abortError = () => new DOMException("Quote read aborted", "AbortError");
  if (opts.signal?.aborted) throw abortError();
  const map = {};
  let lastErr = "";
  let closed = false;
  let aborted = false;
  const stopController = new AbortController();
  let resolveStopped;
  const stopped = new Promise((resolve) => { resolveStopped = resolve; });
  function stop() {
    closed = true;
    stopController.abort();
    resolveStopped();
  }
  const onAbort = () => { aborted = true; stop(); };
  opts.signal?.addEventListener("abort", onAbort, { once: true });
  const timeoutMs = Math.max(1, opts.timeoutMs || 22000);
  const startedAt = Date.now();
  const deadline = startedAt + timeoutMs;
  // Slow RPC tails cannot consume the retry and HTTP fallback budgets.
  const firstDeadline = startedAt + Math.floor(timeoutMs * 0.5);
  const retryDeadline = startedAt + Math.floor(timeoutMs * 0.75);
  const missing = () => list.filter((t) => !map[t]);

  function publish(batch) {
    if (closed || Date.now() >= deadline) return;
    const fresh = {};
    for (const [token, quote] of Object.entries(batch)) {
      if (map[token]) continue;
      map[token] = quote;
      fresh[token] = { ...quote };
    }
    if (!Object.keys(fresh).length || typeof opts.onBatch !== "function") return;
    // Consumer rendering must not block reads or turn a valid quote into an RPC failure.
    try { Promise.resolve(opts.onBatch(fresh)).catch(() => {}); } catch { /* consumer callback */ }
  }

  async function boundedRead(task, stopAt) {
    let timer;
    try {
      return await Promise.race([
        task,
        stopped.then(() => { throw abortError(); }),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error("RPC timeout")), Math.max(1, Math.min(7000, stopAt - Date.now())));
        }),
      ]);
    } finally { clearTimeout(timer); }
  }

  async function readBatches(todo, chunkSize, concurrency, stopAt) {
    const batches = chunk(todo, chunkSize);
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(concurrency, batches.length) }, async () => {
      while (!closed && Date.now() < stopAt && index < batches.length) {
        const batch = batches[index++];
        try {
          const results = await boundedRead(readContracts(wagmiConfig, {
            allowFailure: true,
            contracts: batch.map((token) => ({
              address: FLAP_PORTAL, abi: portalAbi, functionName: "getTokenV5",
              args: [token], chainId: BSC_ID,
            })),
          }), stopAt);
          if (closed) return;
          const fresh = {};
          const observedAt = Date.now();
          const quoteAddresses = results.filter((res) => res?.status === "success").map((res) => res.result?.[9]);
          let decimals = {};
          try {
            const metadataDeadline = Math.min(stopAt, observedAt + 1500);
            decimals = await boundedRead(readQuoteDecimals(quoteAddresses, async (addresses) => {
              const values = await boundedRead(readContracts(wagmiConfig, {
                allowFailure: true,
                contracts: addresses.map((address) => ({ address, abi: quoteDecimalsAbi, functionName: "decimals", chainId: BSC_ID })),
              }), metadataDeadline);
              return values.map((value) => value?.status === "success" ? Number(value.result) : null);
            }), metadataDeadline);
          } catch { /* A readable WAD price does not depend on reserve metadata. */ }
          if (closed) return;
          results.forEach((res, i) => {
            if (res?.status !== "success") {
              if (res?.error) lastErr = String(res.error.shortMessage || res.error.message || res.error);
              return;
            }
            const address = String(res.result?.[9] || "").toLowerCase();
            const parsed = parseTokenV5Result(res.result, decimals[address] ?? knownQuoteDecimals(address));
            if (parsed) fresh[batch[i]] = { ...parsed, quoteUpdatedAt: observedAt, quoteStale: false };
          });
          publish(fresh);
        } catch (e) { if (!closed) lastErr = e?.shortMessage || e?.message || String(e); }
      }
    }));
  }

  async function run() {
    await readBatches(list, Math.max(1, opts.chunkSize || 10), Math.max(1, opts.concurrency || 2), firstDeadline);
    if (!closed && missing().length) await readBatches(missing(), 4, 2, retryDeadline);
    // Backend fallback also fills partial RPC failures, without repeating successful tokens.
    if (closed || !missing().length) return;
    const parts = chunk(missing(), 16);
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(2, parts.length) }, async () => {
      while (!closed && Date.now() < deadline && index < parts.length) {
        const batch = parts[index++];
        try {
          const res = await fetch("/api/quote.php?tokens=" + batch.join(",") + "&t=" + Date.now(), {
            cache: "no-store", signal: AbortSignal.any([
              stopController.signal,
              AbortSignal.timeout(Math.max(1, Math.min(7000, deadline - Date.now()))),
            ]),
          });
          if (!res.ok) throw new Error("quote.php HTTP " + res.status);
          const json = await res.json();
          if (closed) return;
          if (json.error) lastErr = String(json.error);
          const httpDate = Date.parse(res.headers?.get?.("Date") || "");
          const ageMs = Math.max(0, Number(res.headers?.get?.("Age")) || 0) * 1000;
          const responseAt = quoteTimestampMs(json.updatedAt) ||
            (Number.isFinite(httpDate) ? Math.max(0, httpDate - ageMs) : 0);
          const fresh = {};
          for (const [key, q] of Object.entries(json.quotes || {})) {
            const tok = key.toLowerCase();
            if (batch.includes(tok) && q && !q.error) {
              const quoteUpdatedAt = quoteTimestampMs(q.updatedAt) || responseAt;
              fresh[tok] = {
                ...q, source: "quote.php", quoteUpdatedAt,
                quoteStale: !!(json.stale || json.cached || q.stale || q.cached || !quoteUpdatedAt),
              };
            }
          }
          publish(fresh);
        } catch (e) { if (!closed) lastErr = e?.message || String(e); }
      }
    }));
  }

  let timer;
  try {
    await Promise.race([
      run(),
      stopped,
      new Promise((resolve) => { timer = setTimeout(() => { lastErr = "RPC timeout"; stop(); resolve(); }, timeoutMs); }),
    ]);
  } finally {
    stop();
    opts.signal?.removeEventListener("abort", onAbort);
    clearTimeout(timer);
  }
  if (Object.keys(map).length) return map;
  if (aborted) throw abortError();
  throw new Error(lastErr || "RPC unavailable");
}
